import React, { useCallback } from "react";
import { connect } from "react-redux";

import { faPen } from "@fortawesome/pro-solid-svg-icons/faPen";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { defineMessages, useIntl } from "react-intl";

import Currency from "@mapmycustomers/shared/types/Currency";
import useBoolean from "@mapmycustomers/shared/util/hook/useBoolean";
import { Button, LoadingSpinner } from "@mapmycustomers/ui";

import { ReactComponent as BoundariesEnabledIcon } from "@app/assets/map/legends/boundaries/boundariesEnabled.svg";
import { LabeledSwitch } from "@app/component/input/LabeledSwitch";
import { toggleBucketVisibility, toggleVisualizeDemography } from "@app/scene/map/store/actions";
import {
  getBuckets,
  getDataSource,
  isBucketsLoading,
  shouldVisualizeDemography,
} from "@app/scene/map/store/layers/boundaries/selectors";
import Bucket from "@app/scene/map/store/layers/boundaries/types/Bucket";
import DataSource from "@app/scene/map/store/layers/boundaries/types/DataSource";
import {
  doesOrganizationSupportDemographicData,
  doesOrganizationSupportTerritories,
  isCurrentUserMember,
} from "@app/store/iam";
import { getCurrencies } from "@app/store/referenceData";
import { RootState } from "@app/store/rootReducer";
import { useExtendedAnalytics } from "@app/util/analytic/AnalyticsService";
import useAnalytics from "@app/util/contexts/useAnalytics";

import BucketItem from "./BucketItem";
import styles from "./Demography.module.scss";
import DemographyPanel from "./DemographyPanel";
import Upsell from "./Upsell";

const messages = defineMessages({
  configure: {
    id: "map.layer.boundaries.demography.edit.button",
    defaultMessage: "Configure Demographic Data",
    description: "Configure Demographic Data button title",
  },
  [DataSource.MEDIAN_INCOME]: {
    id: "map.layer.boundaries.demography.medianIncome",
    defaultMessage: "Median Income",
    description: "Boundaries visualize data switch title",
  },
  [DataSource.POPULATION]: {
    id: "map.layer.boundaries.demography.population",
    defaultMessage: "Population",
    description: "Boundaries visualize data switch title",
  },
  description: {
    id: "map.layer.boundaries.demography.switch.description",
    defaultMessage:
      "Turn on to see geographies color-coded by demographics like population or median income.",
    description: "Boundaries visualize data switch description",
  },
  enabled: {
    id: "map.layer.boundaries.demography.enabled.description",
    defaultMessage: "Geographic Boundaries are enabled",
    description: "Boundaries enabled description",
  },
  title: {
    id: "map.layer.boundaries.demography.switch.title",
    defaultMessage: "Visualize Demographic Data",
    description: "Boundaries visualize data switch title",
  },
});

interface Props {
  buckets: Bucket[];
  currencies: Currency[];
  dataSource: DataSource;
  demographicDataEnabled: boolean;
  isBucketsLoading: boolean;
  isMember: boolean;
  onToggleBucketVisibility: typeof toggleBucketVisibility;
  onToggleVisualizeDemography: typeof toggleVisualizeDemography;
  territoriesEnabled: boolean;
  visualizeDemography: boolean;
}

export const Demography: React.FC<Props> = ({
  buckets,
  currencies,
  dataSource,
  demographicDataEnabled,
  isBucketsLoading,
  isMember,
  onToggleBucketVisibility,
  onToggleVisualizeDemography,
  territoriesEnabled,
  visualizeDemography,
}) => {
  const intl = useIntl();
  const analytics = useExtendedAnalytics("Boundaries", useAnalytics());
  const [editPanelVisible, showEditPanel, hideEditPanel] = useBoolean();

  const handleToggle = useCallback(() => {
    onToggleVisualizeDemography();
    analytics.clicked(["Visualize demographic data", visualizeDemography ? "on" : "off"]);
  }, [analytics, onToggleVisualizeDemography, visualizeDemography]);

  const handleClickConfigure = useCallback(() => {
    (editPanelVisible ? hideEditPanel : showEditPanel)();
    analytics.clicked(["Configure demographic data"]);
  }, [analytics, editPanelVisible, hideEditPanel, showEditPanel]);

  return (
    <>
      {demographicDataEnabled ? (
        <div className={styles.container}>
          <LabeledSwitch
            checked={visualizeDemography}
            labelClassName={styles.labeledSwitch}
            onChange={handleToggle}
            title={intl.formatMessage(messages.title)}
          />
          {visualizeDemography ? (
            isBucketsLoading ? (
              <LoadingSpinner />
            ) : (
              <>
                <span>{intl.formatMessage(messages[dataSource])}</span>
                <div className={styles.list}>
                  {buckets.map((bucket) => (
                    <BucketItem
                      bucket={bucket}
                      currencies={currencies}
                      key={bucket.id}
                      onToggleChange={onToggleBucketVisibility}
                    />
                  ))}
                </div>
              </>
            )
          ) : (
            <span className={styles.description}>{intl.formatMessage(messages.description)}</span>
          )}
        </div>
      ) : (
        <div className={styles.onlyBoundaries}>
          <BoundariesEnabledIcon />
          <span className={styles.enabled}>{intl.formatMessage(messages.enabled)}</span>
        </div>
      )}
      {visualizeDemography && editPanelVisible && demographicDataEnabled && (
        <DemographyPanel onHide={hideEditPanel} />
      )}
      {visualizeDemography && (
        <Button
          className={styles.editButton}
          icon={<FontAwesomeIcon icon={faPen} />}
          iconOnly
          onClick={handleClickConfigure}
          type="filled"
        >
          {intl.formatMessage(messages.configure)}
        </Button>
      )}
      {!territoriesEnabled && !visualizeDemography && !isMember && (
        <Upsell demographicDataEnabled={demographicDataEnabled} />
      )}
    </>
  );
};

const mapStateToProps = (state: RootState) => ({
  buckets: getBuckets(state),
  currencies: getCurrencies(state),
  dataSource: getDataSource(state),
  demographicDataEnabled: doesOrganizationSupportDemographicData(state),
  isBucketsLoading: isBucketsLoading(state),
  isMember: isCurrentUserMember(state),
  territoriesEnabled: doesOrganizationSupportTerritories(state),
  visualizeDemography: shouldVisualizeDemography(state),
});

const mapDispatchToProps = {
  onToggleBucketVisibility: toggleBucketVisibility,
  onToggleVisualizeDemography: toggleVisualizeDemography,
};

export default connect(mapStateToProps, mapDispatchToProps)(Demography);
